import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Copy, Sparkles, FileText, User, Briefcase, Award, Hash, Image, MessageSquare, BookOpen, GraduationCap, Heart, CheckCircle, Download, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { WordOnlineViewer } from "@/components/WordOnlineViewer";
import { downloadWordDocument, WordDocumentData } from "@/utils/linkedinDocumentUtils";
import { supabase } from "@/integrations/supabase/client";
import { LinkedInProfilePackage, exportLinkedInPackagePdf, LinkedInPackageData } from "@/components/LinkedInProfilePackage";

interface LinkedInWizardResultsProps {
  profile: any;
  linkedinUrl?: string;
  photoUrl?: string;
  onBack: () => void;
  onStartOver: () => void;
  onViewReport?: () => void;
  wordDocument?: WordDocumentData | null;
}

interface ParsedContent {
  firstName: string;
  lastName: string;
  headlineOption1: string;
  headlineOption2: string;
  aboutSection: string;
  professionalExperience: string[];
  skills: string[];
  featuredSection: string[];
  recommendationTemplates: string[];
  volunteerExperience: string;
  certifications: string;
  education: string;
  accomplishments: string;
  hashtagStrategy: string[];
  visualBranding: string;
  activityStrategy: string[];
  rawContent: string;
}

export function LinkedInWizardResults({ profile, linkedinUrl, photoUrl, onBack, onStartOver, onViewReport, wordDocument }: LinkedInWizardResultsProps) {
  const [packageData, setPackageData] = useState<LinkedInPackageData | null>(null);
  const [packageBusy, setPackageBusy] = useState(false);
  const packageRef = useRef<HTMLDivElement>(null);
  const [parsedContent, setParsedContent] = useState<ParsedContent | null>(null);
  const { toast } = useToast();
  
  // Determine default tab based on whether Word document is available
  const defaultTab = wordDocument?.publicUrl ? "document" : "raw";

  useEffect(() => {
    if (profile?.rawContent) {
      const parsed = parseLinkedInContent(profile.rawContent);
      setParsedContent(parsed);
    }
  }, [profile]);

  const parseLinkedInContent = (content: string): ParsedContent => {
    // Helper function to extract section content
    const extractSection = (sectionName: string, nextSections: string[] = []): string => {
      const patterns = [
        `\\*\\*${sectionName}:\\*\\*`,
        `\\*\\*${sectionName}\\*\\*:`,
        `### ${sectionName}`,
        `## ${sectionName}`,
        `${sectionName}:`
      ];
      
      for (const pattern of patterns) {
        const regex = new RegExp(`${pattern}\\s*([\\s\\S]*?)(?=${nextSections.map(s => `\\*\\*${s}|### ${s}|## ${s}`).join('|')}|$)`, 'i');
        const match = content.match(regex);
        if (match && match[1]?.trim()) {
          return match[1].trim();
        }
      }
      return '';
    };

    // Extract First Name and Last Name
    const firstNameMatch = content.match(/\*\*First Name:\*\*\s*(.+?)(?:\n|$)/i);
    const lastNameMatch = content.match(/\*\*Last Name:\*\*\s*(.+?)(?:\n|$)/i);
    
    // Extract Headline Options
    const headlineOption1Match = content.match(/\*\*Headline Option 1:\*\*\s*(.+?)(?:\n\n|\*\*Headline Option 2|$)/is);
    const headlineOption2Match = content.match(/\*\*Headline Option 2:\*\*\s*(.+?)(?:\n\n|---|$)/is);
    
    // Extract About Section
    const aboutSection = extractSection('ABOUT SECTION', ['PROFESSIONAL EXPERIENCE', 'SKILLS']);
    
    // Extract Professional Experience
    const experienceSection = extractSection('PROFESSIONAL EXPERIENCE SECTION', ['ADDITIONAL PROFILE', 'SKILLS']);
    const experiences = experienceSection.split(/\*\*Title:\*\*/i).filter(exp => exp.trim().length > 0);
    
    // Extract Skills
    const skillsSection = extractSection('Skills Optimization', ['Featured Section', 'Recommendation']);
    const skills = skillsSection.split(/[,\n]/).map(s => s.replace(/^[-•*]\s*/, '').trim()).filter(s => s.length > 0 && s.length < 50);
    
    // Extract Featured Section
    const featuredSection = extractSection('Featured Section', ['Recommendation Templates', 'Volunteer']);
    const featuredItems = featuredSection.split(/\n/).map(s => s.replace(/^[-•*\d.]\s*/, '').trim()).filter(s => s.length > 0);
    
    // Extract Recommendation Templates
    const recommendationSection = extractSection('Recommendation Templates', ['Volunteer Experience', 'Certifications']);
    const recommendations = recommendationSection.split(/Template \d|Email \d/i).filter(t => t.trim().length > 0);
    
    // Extract other sections
    const volunteerExperience = extractSection('Volunteer Experience', ['Certifications', 'Education']);
    const certifications = extractSection('Certifications & Licenses', ['Education', 'Accomplishments']);
    const education = extractSection('Education Enhancements', ['Accomplishments', 'Hashtag']);
    const accomplishments = extractSection('Accomplishments Section', ['Hashtag Strategy', 'Visual']);
    
    // Extract Hashtag Strategy
    const hashtagSection = extractSection('Hashtag Strategy', ['Visual Branding', 'Activity Strategy']);
    const hashtags = hashtagSection.split(/[,\n]/).map(h => h.replace(/^[-•*]\s*/, '').trim()).filter(h => h.startsWith('#') || h.length > 0);
    
    // Extract Visual Branding
    const visualBranding = extractSection('Visual Branding', ['Activity Strategy']);
    
    // Extract Activity Strategy
    const activitySection = extractSection('Activity Strategy', []);
    const activityItems = activitySection.split(/\n\d+\.|Post \d/i).map(s => s.replace(/^[-•*]\s*/, '').trim()).filter(s => s.length > 0);

    return {
      firstName: firstNameMatch?.[1]?.trim() || '',
      lastName: lastNameMatch?.[1]?.trim() || '',
      headlineOption1: headlineOption1Match?.[1]?.trim() || profile.headline || '',
      headlineOption2: headlineOption2Match?.[1]?.trim() || '',
      aboutSection: aboutSection || profile.summary || '',
      professionalExperience: experiences.length > 0 ? experiences : profile.experience || [],
      skills: skills.length > 0 ? skills : profile.skills || [],
      featuredSection: featuredItems,
      recommendationTemplates: recommendations,
      volunteerExperience,
      certifications,
      education,
      accomplishments,
      hashtagStrategy: hashtags,
      visualBranding,
      activityStrategy: activityItems,
      rawContent: content
    };
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${type} copied to clipboard.`,
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Please select and copy the text manually.",
        variant: "destructive"
      });
    }
  };

  // Branded 10-page LinkedIn Profile Package PDF: format the generated content
  // into the package schema server-side, render the navy design, screenshot
  // each page at 2x, and assemble an image-based Letter PDF.
  const handleDownloadPackage = async () => {
    if (!parsedContent) return;
    setPackageBusy(true);
    try {
      const fullName = `${parsedContent.firstName} ${parsedContent.lastName}`.trim() || 'Client';
      const { data, error } = await supabase.functions.invoke('generate-linkedin-document', {
        body: { action: 'package-json', rawContent: profile?.rawContent ?? parsedContent.rawContent, fullName, linkedinUrl: linkedinUrl ?? '' },
      });
      if (error) throw error;
      if (!data?.package) throw new Error(data?.error || 'Package formatting returned nothing');
      if (data.qa && !data.qa.pass) {
        toast({ title: 'Package QA flags', description: data.qa.violations.join(' | '), variant: 'destructive' });
      }
      const pkg = data.package as LinkedInPackageData;
      pkg.client.photoUrl = photoUrl || undefined;
      setPackageData(pkg);
      // Let the hidden package render + fonts settle, then export.
      await new Promise((r) => setTimeout(r, 2500));
      if (!packageRef.current) throw new Error('package did not render');
      await exportLinkedInPackagePdf(packageRef.current, pkg.client.fullName);
      toast({ title: 'Download started', description: `${pkg.client.fullName} - LinkedIn Profile Package.pdf` });
    } catch (e: any) {
      toast({ title: 'Package export failed', description: e?.message ?? String(e), variant: 'destructive' });
    } finally {
      setPackageBusy(false);
    }
  };

  const handleDownloadWord = () => {
    if (wordDocument?.data && wordDocument?.filename) {
      downloadWordDocument(wordDocument.data, wordDocument.filename);
      toast({
        title: "Download started",
        description: `${wordDocument.filename} is downloading.`,
      });
    }
  };

  if (!parsedContent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={onBack} 
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-green-600" />
            <span className="font-semibold text-green-600">LinkedIn Profile Generated!</span>
          </div>
        </div>

        {/* Tabs for different sections */}
        <Tabs defaultValue={defaultTab} className="w-full">
          <div className="overflow-x-auto pb-2">
            <TabsList className="inline-flex w-max min-w-full gap-1 h-auto p-1">
              {wordDocument?.publicUrl && (
                <TabsTrigger value="document" className="text-xs font-bold bg-green-500/10 data-[state=active]:bg-green-600 data-[state=active]:text-white px-3 py-2 whitespace-nowrap">
                  📄 Word Document
                </TabsTrigger>
              )}
              <TabsTrigger value="raw" className="text-xs font-bold bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-2 whitespace-nowrap">
                ⭐ Full Content
              </TabsTrigger>
              <TabsTrigger value="headlines" className="text-xs px-3 py-2 whitespace-nowrap">Headlines</TabsTrigger>
              <TabsTrigger value="about" className="text-xs px-3 py-2 whitespace-nowrap">About</TabsTrigger>
              <TabsTrigger value="experience" className="text-xs px-3 py-2 whitespace-nowrap">Experience</TabsTrigger>
              <TabsTrigger value="skills" className="text-xs px-3 py-2 whitespace-nowrap">Skills</TabsTrigger>
              <TabsTrigger value="featured" className="text-xs px-3 py-2 whitespace-nowrap">Featured</TabsTrigger>
              <TabsTrigger value="recommendations" className="text-xs px-3 py-2 whitespace-nowrap">Recs</TabsTrigger>
              <TabsTrigger value="extras" className="text-xs px-3 py-2 whitespace-nowrap">Extras</TabsTrigger>
              <TabsTrigger value="strategy" className="text-xs px-3 py-2 whitespace-nowrap">Strategy</TabsTrigger>
            </TabsList>
          </div>

          {/* Word Document Tab */}
          {wordDocument?.publicUrl && (
            <TabsContent value="document">
              <Card className="border-2 border-green-500/20">
                <CardHeader className="bg-green-500/5">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5 text-green-600" />
                      LinkedIn Update Instructions
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadWord}
                      className="border-green-500/50 hover:bg-green-500/10"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download Word
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={packageBusy}
                      onClick={handleDownloadPackage}
                      className="border-blue-900/50 hover:bg-blue-900/10 ml-2"
                    >
                      {packageBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      Profile Package (PDF)
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-[600px]">
                    <WordOnlineViewer
                      documentUrl={wordDocument.publicUrl}
                      fileName={wordDocument.filename || 'LinkedIn Profile.docx'}
                      height="100%"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Headlines Tab */}
          <TabsContent value="headlines" className="space-y-4">
            {/* Name Formatting */}
            {(parsedContent.firstName || parsedContent.lastName) && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <User className="w-5 h-5" />
                      Name Formatting
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(`${parsedContent.firstName}\n${parsedContent.lastName}`, "Name")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <span className="text-sm text-muted-foreground">First Name:</span>
                    <p className="font-medium">{parsedContent.firstName}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Last Name:</span>
                    <p className="font-medium">{parsedContent.lastName}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Headline Option 1 */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Badge className="bg-blue-600">Option 1</Badge>
                    Problem-Solving Headline
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(parsedContent.headlineOption1, "Headline Option 1")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="font-medium text-lg">{parsedContent.headlineOption1}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Character count: {parsedContent.headlineOption1.length}/220
                  {parsedContent.headlineOption1.length > 220 && (
                    <span className="text-red-500 ml-2">(Over limit!)</span>
                  )}
                </p>
              </CardContent>
            </Card>

            {/* Headline Option 2 */}
            {parsedContent.headlineOption2 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Badge variant="secondary">Option 2</Badge>
                      Professional Titles Headline
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(parsedContent.headlineOption2, "Headline Option 2")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="font-medium text-lg">{parsedContent.headlineOption2}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Character count: {parsedContent.headlineOption2.length}/220
                    {parsedContent.headlineOption2.length > 220 && (
                      <span className="text-red-500 ml-2">(Over limit!)</span>
                    )}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* About Tab */}
          <TabsContent value="about">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5" />
                    About Section (5 Paragraphs)
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(parsedContent.aboutSection, "About Section")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="whitespace-pre-line text-sm leading-relaxed pr-4">
                    {parsedContent.aboutSection}
                  </div>
                </ScrollArea>
                <Separator className="my-4" />
                <p className="text-sm text-muted-foreground">
                  Character count: {parsedContent.aboutSection.length}/2,600
                  {parsedContent.aboutSection.length > 2600 && (
                    <span className="text-red-500 ml-2">(Over limit!)</span>
                  )}
                  {parsedContent.aboutSection.length >= 2000 && parsedContent.aboutSection.length <= 2500 && (
                    <span className="text-green-500 ml-2">(Optimal length!)</span>
                  )}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Experience Tab */}
          <TabsContent value="experience" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="w-5 h-5" />
                  Professional Experience
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-6 pr-4">
                    {parsedContent.professionalExperience.map((exp, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold">Experience #{index + 1}</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(typeof exp === 'string' ? exp : JSON.stringify(exp), `Experience ${index + 1}`)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="whitespace-pre-line text-sm">
                          {typeof exp === 'string' ? exp : JSON.stringify(exp, null, 2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Skills Tab */}
          <TabsContent value="skills">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="w-5 h-5" />
                    Skills ({parsedContent.skills.length}/50)
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(parsedContent.skills.join(", "), "Skills")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {parsedContent.skills.slice(0, 50).map((skill, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Featured Tab */}
          <TabsContent value="featured">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="w-5 h-5" />
                    Featured Section Items
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(parsedContent.featuredSection.join("\n\n"), "Featured Section")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {parsedContent.featuredSection.length > 0 ? (
                    parsedContent.featuredSection.map((item, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm">{item}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">No featured section items generated.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MessageSquare className="w-5 h-5" />
                    Recommendation Request Templates
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(parsedContent.recommendationTemplates.join("\n\n---\n\n"), "Recommendation Templates")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4 pr-4">
                    {parsedContent.recommendationTemplates.length > 0 ? (
                      parsedContent.recommendationTemplates.map((template, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge>Template #{index + 1}</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(template, `Template ${index + 1}`)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-sm whitespace-pre-line">{template}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-sm">No recommendation templates generated.</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Extras Tab */}
          <TabsContent value="extras" className="space-y-4">
            {/* Volunteer Experience */}
            {parsedContent.volunteerExperience && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Heart className="w-5 h-5" />
                      Volunteer Experience
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(parsedContent.volunteerExperience, "Volunteer Experience")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{parsedContent.volunteerExperience}</p>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {parsedContent.certifications && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Award className="w-5 h-5" />
                      Certifications & Licenses
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(parsedContent.certifications, "Certifications")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{parsedContent.certifications}</p>
                </CardContent>
              </Card>
            )}

            {/* Education */}
            {parsedContent.education && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <GraduationCap className="w-5 h-5" />
                      Education Enhancements
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(parsedContent.education, "Education")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{parsedContent.education}</p>
                </CardContent>
              </Card>
            )}

            {/* Accomplishments */}
            {parsedContent.accomplishments && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Award className="w-5 h-5" />
                      Accomplishments
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(parsedContent.accomplishments, "Accomplishments")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{parsedContent.accomplishments}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Strategy Tab */}
          <TabsContent value="strategy" className="space-y-4">
            {/* Hashtag Strategy */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Hash className="w-5 h-5" />
                    Hashtag Strategy ({parsedContent.hashtagStrategy.length} hashtags)
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(parsedContent.hashtagStrategy.join(" "), "Hashtags")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {parsedContent.hashtagStrategy.map((hashtag, index) => (
                    <Badge key={index} variant="outline" className="text-blue-600">
                      {hashtag.startsWith('#') ? hashtag : `#${hashtag}`}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Visual Branding */}
            {parsedContent.visualBranding && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Image className="w-5 h-5" />
                      Visual Branding Guidance
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(parsedContent.visualBranding, "Visual Branding")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{parsedContent.visualBranding}</p>
                </CardContent>
              </Card>
            )}

            {/* Activity Strategy */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5" />
                    Activity Strategy (Post Ideas)
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(parsedContent.activityStrategy.join("\n\n"), "Activity Strategy")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {parsedContent.activityStrategy.length > 0 ? (
                    parsedContent.activityStrategy.map((idea, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0">
                          {index + 1}
                        </span>
                        <p className="text-sm">{idea}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">No activity strategy items generated.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Full Content Tab - Shows complete AI output without parsing */}
          <TabsContent value="raw">
            <Card className="border-2 border-primary/20">
              <CardHeader className="bg-primary/5">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-primary" />
                    Full Generated Output (Unparsed)
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(parsedContent.rawContent, "Full Content")}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const blob = new Blob([parsedContent.rawContent], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'linkedin-profile-content.txt';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download .txt
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <ScrollArea className="h-[600px]">
                  <div className="whitespace-pre-wrap text-sm font-mono bg-muted/30 p-4 rounded-lg pr-4">
                    {parsedContent.rawContent}
                  </div>
                </ScrollArea>
                <Separator className="my-4" />
                <p className="text-sm text-muted-foreground">
                  Total characters: {parsedContent.rawContent.length}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center pt-6">
          <Button variant="outline" onClick={onBack}>
            Back to Content Review
          </Button>
          {onViewReport && (
            <Button variant="outline" onClick={onViewReport}>
              <FileText className="w-4 h-4 mr-2" />
              View Professional Report
            </Button>
          )}
          <Button onClick={onStartOver}>
            Create Another Profile
          </Button>
        </div>
      </div>

      {/* Off-screen render of the branded Profile Package for PDF export */}
      {packageData && (
        <div style={{ position: 'fixed', left: -20000, top: 0, width: 1275 }} aria-hidden>
          <div ref={packageRef}>
            <LinkedInProfilePackage data={packageData} />
          </div>
        </div>
      )}
    </div>
  );
}
